import { createModel } from '@rematch/core';
import { generatePath } from 'react-router-dom';
import { toast } from 'react-toastify';

// Constants
import URL from '../../constants/api-constants';

// Types
import type { RootModel } from './index';

// Http
import http from '../http/http-common';
import { Cycle } from './cycles';
import { Organization } from './organizations';
import { Location } from './locations';
import { IControllerSettings, IFaultControlSetup } from '../../types/controller-types';
import CycleMeasurement from '../../enums/cycle-measurement';
import axios, { AxiosError, AxiosRequestConfig, GenericAbortSignal } from 'axios';

export type CycleController = {
  id: string;
  cycleId: string;
  controllerId: string;
  channels: string[];
  thermocouples: string[];
  created_at?: string;
  updated_at?: string;
  controller?: Controller;
  cycle?: Cycle;
  location?: Location;
};

export type Controller = {
  id: string;
  name: string;
  serial_number: string;
  is_active: boolean;
  calcerts_not_needed: boolean;
  calcerts_not_needed_updated_by?: string | null;
  calcerts_not_needed_updated_at?: string | null;
  created_at?: Date;
  updated_at?: Date;
  cycle_controllers?: CycleController[];
  locationId: string | null;
  location?: Location | null;
  organizationId: string | null;
  organization?: Organization | null;
  units?: CycleMeasurement | null;
  connected: boolean;
};

export type IotController = Controller & {
  ip_address: string | null;
  firmware_version: string | null;
};

const DEFAULT_STATE: {
  controllers: Controller[];
  controllers_list: IotController[];
  controller: Controller | null;
  controller_settings: IControllerSettings | null;
  fault_control: IFaultControlSetup[];
  fault_control_error: string | null;
} = {
  controllers: [],
  controllers_list: [],
  controller: null,
  controller_settings: null,
  fault_control: [],
  fault_control_error: null,
};

export const controllers = createModel<RootModel>()({
  state: DEFAULT_STATE,
  reducers: {
    set(state, payload) {
      return {
        ...state,
        ...payload,
      };
    },
    setController(state, payload) {
      return {
        ...state,
        controller: payload,
      };
    },
    setControllerList(state, payload) {
      return {
        ...state,
        controllers: payload,
      };
    },
    setIotControllerList(state, payload) {
      return {
        ...state,
        controllers_list: payload,
      };
    },
    resetOne(state) {
      return {
        ...state,
        controller: DEFAULT_STATE.controller,
      };
    },
    resetSettings(state) {
      return {
        ...state,
        controller_settings: DEFAULT_STATE.controller_settings,
      };
    },
    resetList(state) {
      return {
        ...state,
        controllers: DEFAULT_STATE.controllers,
      };
    },
    resetIotControllersList(state) {
      return {
        ...state,
        controllers_list: DEFAULT_STATE.controllers_list,
      };
    },
    resetState() {
      return DEFAULT_STATE;
    },
  },
  effects: (dispatch) => {
    return {
      async getControllers() {
        try {
          const response = await http(URL.CONTROLLERS);

          if (response.status === 200) {
            dispatch.controllers.setIotControllerList(response.data);
          }
        } catch (error: any) {
          toast.error(error.message);
        }
      },

      async getController({ id, signal }: { id: string; signal?: GenericAbortSignal }) {
        try {
          const path = generatePath(URL.CONTROLLER, { id });
          const config: AxiosRequestConfig<{ signal?: GenericAbortSignal }> = {};

          if (signal) {
            config.signal = signal;
          }

          const response = await http.get(path, config);

          dispatch.controllers.setController(response.data);

          return {
            controller: response.data,
          };
        } catch (error) {
          if (axios.isCancel(error)) {
            return;
          }

          if (error instanceof AxiosError) {
            toast.error(error.response?.data?.message || 'An unexpected error occurred');
          } else if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error('An unexpected error occurred');
          }
          throw error;
        }
      },

      async getAllByLocationId(locationId: string) {
        try {
          const response = await http.get(generatePath(URL.CONTROLLERS_LOCATION, { locationId }));
          dispatch.controllers.setControllerList(response.data);

          return response;
        } catch (error) {
          toast.error(error.message);
        }
      },

      async getAllByOrganizationId(organizationId: string) {
        try {
          const response = await http.get(generatePath(URL.CONTROLLERS_ORGANIZATION, { organizationId }));
          dispatch.controllers.setControllerList(response.data);

          return response;
        } catch (error) {
          toast.error(error.message);
        }
      },

      async getControllerSettings(id: string) {
        try {
          const response = await http.get<IControllerSettings>(generatePath(URL.CONTROLLER_SETTINGS, { id }));
          dispatch.controllers.set({
            controller_settings: response.data,
          });

          return {
            controller_settings: response.data,
          };
        } catch (error) {
          toast.error(error.message);
        }
      },

      async updateController({ id, formData }) {
        try {
          const response = await http.put(generatePath(URL.CONTROLLER, { id }), formData);
          dispatch.controllers.setController(response.data);
          toast.success('Controller updated successfully');
        } catch (error) {
          toast.error(error.message);
        }
      },

      async getControllerFaultControl(id: string) {
        try {
          const response = await http.get(generatePath(URL.CONTROLLER_FAULT_CONTROL, { id }));
          dispatch.controllers.set({
            fault_control: response.data.data,
            fault_control_error: null,
          });

          return {
            fault_control: response.data.data,
          };
        } catch (error: any) {
          // error.message is just Axios's generic "Request failed with status
          // code 500" - the backend's actual diagnostic message lives on the
          // response body, which is what the Fault Control page displays.
          const detailedMessage = error?.response?.data?.message || error.message;
          dispatch.controllers.set({
            fault_control_error: detailedMessage,
          });
          toast.error(detailedMessage);
        }
      },

      async getSetupReconciliationStatus(controllerId: string) {
        try {
          const response = await http.get(
            generatePath(URL.CONTROLLER_SETUP_RECONCILIATION, { controllerId }),
          );
          return response.data as {
            active: boolean;
            nextCheckAt: string | null;
            lastError: string | null;
            lastMismatches: string[] | null;
            attempts: number;
            maxAttempts: number;
            gaveUp: boolean;
          };
        } catch (error) {
          return undefined;
        }
      },

      async stopSetupReconciliation(controllerId: string) {
        try {
          await http.post(generatePath(URL.CONTROLLER_SETUP_RECONCILIATION_STOP, { controllerId }));
          return true;
        } catch (error: any) {
          toast.error(error.message);
          return false;
        }
      },
    };
  },
});
